import React from "react";
import { Box, Container, VStack, Grid, Heading, Text, HStack } from "@chakra-ui/react";
import Navbar from "../components/common/Navbar";
import Footer from "../components/common/Footer";
import Hero from "../components/common/Hero";
import Card from "../components/common/Card";
import CTAButton from "../components/common/CTAButton";
import { FaChartLine, FaTrophy, FaUsers, FaChartBar, FaAward, FaHandshake } from "react-icons/fa";

export default function ForColleges() {
  const benefits = [
    {
      icon: FaTrophy,
      title: "Improve Placement Outcomes",
      description: "85%+ of our students land jobs within 3 months. Your placements improve significantly.",
    },
    {
      icon: FaChartLine,
      title: "Real-Time Dashboard",
      description: "Monitor student progress, skill development, and placement pipeline in real-time.",
    },
    {
      icon: FaUsers,
      title: "Industry Partnerships",
      description: "Direct access to 1000+ hiring partners actively looking for your graduates.",
    },
    {
      icon: FaChartBar,
      title: "Placement Metrics",
      description: "Transparent reporting on placement rates, salary packages, and job satisfaction.",
    },
    {
      icon: FaAward,
      title: "Brand Enhancement",
      description: "Demonstrate modern college outcomes. Boost enrollment and college reputation.",
    },
    {
      icon: FaHandshake,
      title: "Zero Conflict Model",
      description: "Collaborate with us, don't compete. Your faculty stays involved, gets upskilled.",
    },
  ];

  const dashboardFeatures = [
    "Real-time student skill assessments",
    "Project portfolio tracking",
    "Job placement pipeline visibility",
    "Salary statistics and trends",
    "Interview preparation progress",
    "Industry partner engagement metrics",
  ];

  const partnershipSteps = [
    {
      number: "1",
      title: "Consultation",
      description: "Understand your college's needs, student demographics, and placement goals.",
    },
    {
      number: "2",
      title: "Program Customization",
      description: "We tailor the program to match your curriculum and academic calendar.",
    },
    {
      number: "3",
      title: "Pilot Phase",
      description: "Start with one discipline and 50-100 students for 3-6 months.",
    },
    {
      number: "4",
      title: "Scale & Optimize",
      description: "Expand to all disciplines based on proven outcomes and feedback.",
    },
  ];

  return (
    <Box>
      <Navbar />

      {/* Hero Section */}
      <Hero
        title="Empower Your Students. Elevate Your Placements."
        subtitle="Stop watching your top talent go unemployed. We help colleges create graduates companies actually want to hire."
        badge="Partnership Opportunity"
        withGradient
      >
        <HStack spacing={4} justify="center" flexWrap="wrap">
          <CTAButton href="mailto:partnerships@ojasamirai.com" size="lg">
            Schedule a Discussion
          </CTAButton>
          <CTAButton to="/" variant="outline" size="lg">
            Back to Home
          </CTAButton>
        </HStack>
      </Hero>

      {/* The Challenge Section */}
      <Box py={{ base: 12, md: 20 }} bg="white">
        <Container maxW="container.xl">
          <VStack spacing={12}>
            <VStack spacing={4} textAlign="center">
              <Heading as="h2" size="2xl" color="#1E293B">
                The College Placement Challenge
              </Heading>
              <Text fontSize="lg" color="#475569" maxW="2xl">
                Your students work hard, your faculty teaches well, but placements remain a challenge. We fix that.
              </Text>
            </VStack>

            <Grid templateColumns={{ base: "1fr", md: "repeat(2, 1fr)" }} gap={8} w="full">
              <Box p={6} border="1px solid #E2E8F0" borderRadius="lg">
                <Heading size="sm" color="#E74C3C" mb={3}>
                  The Problem
                </Heading>
                <VStack align="start" spacing={2}>
                  <Text color="#475569">• Industry expects practical skills, you teach theory</Text>
                  <Text color="#475569">• Your placements lag behind peer colleges</Text>
                  <Text color="#475569">• Top students leave for coaching centers</Text>
                  <Text color="#475569">• No visibility into job market trends</Text>
                  <Text color="#475569">• Faculty lacks industry engagement</Text>
                </VStack>
              </Box>

              <Box p={6} border="1px solid #E2E8F0" borderRadius="lg" bg="#ECFDF5">
                <Heading size="sm" color="#10B981" mb={3}>
                  Our Solution
                </Heading>
                <VStack align="start" spacing={2}>
                  <Text color="#475569">✓ Industry-aligned curriculum mapped to job roles</Text>
                  <Text color="#475569">✓ Real projects from actual companies</Text>
                  <Text color="#475569">✓ Direct access to 1000+ hiring partners</Text>
                  <Text color="#475569">✓ Real-time placement metrics dashboard</Text>
                  <Text color="#475569">✓ Faculty upskilling & co-teaching support</Text>
                </VStack>
              </Box>
            </Grid>
          </VStack>
        </Container>
      </Box>

      {/* Partnership Benefits */}
      <Box bg="#F8FAFC" py={{ base: 12, md: 20 }}>
        <Container maxW="container.xl">
          <VStack spacing={12}>
            <VStack spacing={4} textAlign="center">
              <Heading as="h2" size="2xl" color="#1E293B">
                Partnership Benefits
              </Heading>
              <Text fontSize="lg" color="#475569" maxW="2xl">
                What your college gains from this partnership.
              </Text>
            </VStack>

            <Grid
              templateColumns={{ base: "1fr", md: "repeat(2, 1fr)", lg: "repeat(3, 1fr)" }}
              gap={8}
              w="full"
            >
              {benefits.map((item, idx) => (
                <Card
                  key={idx}
                  icon={item.icon}
                  title={item.title}
                  description={item.description}
                />
              ))}
            </Grid>
          </VStack>
        </Container>
      </Box>

      {/* Partnership Model */}
      <Box py={{ base: 12, md: 20 }}>
        <Container maxW="container.xl">
          <VStack spacing={12}>
            <VStack spacing={4} textAlign="center">
              <Heading as="h2" size="2xl" color="#1E293B">
                How the Partnership Works
              </Heading>
              <Text fontSize="lg" color="#475569" maxW="2xl">
                A structured 4-phase approach to ensure success.
              </Text>
            </VStack>

            <Grid templateColumns={{ base: "1fr", md: "repeat(2, 1fr)", lg: "repeat(4, 1fr)" }} gap={6} w="full">
              {partnershipSteps.map((step, idx) => (
                <Box key={idx} p={6} bg="white" border="2px solid #10B981" borderRadius="lg" textAlign="center">
                  <Box fontSize="3xl" fontWeight="bold" color="#10B981" mb={4}>
                    {step.number}
                  </Box>
                  <Heading size="sm" color="#1E293B" mb={3}>
                    {step.title}
                  </Heading>
                  <Text fontSize="sm" color="#475569">
                    {step.description}
                  </Text>
                </Box>
              ))}
            </Grid>
          </VStack>
        </Container>
      </Box>

      {/* Dashboard Section */}
      <Box bg="#F8FAFC" py={{ base: 12, md: 20 }}>
        <Container maxW="container.xl">
          <VStack spacing={12}>
            <VStack spacing={4} textAlign="center">
              <Heading as="h2" size="2xl" color="#1E293B">
                Real-Time Analytics Dashboard
              </Heading>
              <Text fontSize="lg" color="#475569" maxW="2xl">
                Complete visibility into your students' progress and placement pipeline.
              </Text>
            </VStack>

            <Grid templateColumns={{ base: "1fr", md: "repeat(2, 1fr)" }} gap={12} w="full" alignItems="center">
              <Box>
                <VStack align="start" spacing={6}>
                  <Box>
                    <Heading size="md" color="#1E293B" mb={2}>
                      Track Everything in Real-Time
                    </Heading>
                    <Text color="#475569">
                      Monitor student progress from enrollment to job placement. See what's working and what needs improvement.
                    </Text>
                  </Box>

                  <VStack align="start" spacing={3}>
                    {dashboardFeatures.map((feature, idx) => (
                      <HStack key={idx} spacing={3}>
                        <Box color="#10B981" fontSize="lg">
                          ✓
                        </Box>
                        <Text color="#475569">{feature}</Text>
                      </HStack>
                    ))}
                  </VStack>
                </VStack>
              </Box>

              <Box p={8} bg="white" borderRadius="lg" border="1px solid #E2E8F0">
                <Box fontSize="2xl" fontWeight="bold" color="#10B981" mb={4}>
                  Sample Dashboard Metrics
                </Box>
                <VStack align="start" spacing={3}>
                  <HStack spacing={4}>
                    <Box>
                      <Box fontSize="2xl" fontWeight="bold" color="#1E293B">
                        85%+
                      </Box>
                      <Text fontSize="sm" color="#475569">
                        Placement Rate
                      </Text>
                    </Box>
                    <Box>
                      <Box fontSize="2xl" fontWeight="bold" color="#1E293B">
                        3 years
                      </Box>
                      <Text fontSize="sm" color="#475569">
                        Total Training time
                      </Text>
                    </Box>
                  </HStack>
                  <HStack spacing={4}>
                    <Box>
                      <Box fontSize="2xl" fontWeight="bold" color="#1E293B">
                        4.5L+
                      </Box>
                      <Text fontSize="sm" color="#475569">
                        Avg. First Salary
                      </Text>
                    </Box>
                    <Box>
                      <Box fontSize="2xl" fontWeight="bold" color="#1E293B">
                        92%
                      </Box>
                      <Text fontSize="sm" color="#475569">
                        Job Satisfaction
                      </Text>
                    </Box>
                  </HStack>
                </VStack>
              </Box>
            </Grid>
          </VStack>
        </Container>
      </Box>

      {/* FAQ Section */}
      <Box py={{ base: 12, md: 20 }}>
        <Container maxW="container.xl">
          <VStack spacing={12}>
            <VStack spacing={4} textAlign="center">
              <Heading as="h2" size="2xl" color="#1E293B">
                Common Questions
              </Heading>
              <Text fontSize="lg" color="#475569" maxW="2xl">
                Everything you need to know about the partnership.
              </Text>
            </VStack>

            <Grid templateColumns={{ base: "1fr", md: "repeat(2, 1fr)" }} gap={8} w="full">
              <Box p={6} bg="#F8FAFC" borderRadius="lg">
                <Heading size="sm" color="#1E293B" mb={3}>
                  Will this replace our curriculum?
                </Heading>
                <Text color="#475569">
                  No. We work alongside your existing curriculum. Our program augments what you teach with practical, industry-relevant skills.
                </Text>
              </Box>

              <Box p={6} bg="#F8FAFC" borderRadius="lg">
                <Heading size="sm" color="#1E293B" mb={3}>
                  What's the cost?
                </Heading>
                <Text color="#475569">
                  No upfront cost. We charge a small fee per successful placement. You only pay when we deliver results.
                </Text>
              </Box>

              <Box p={6} bg="#F8FAFC" borderRadius="lg">
                <Heading size="sm" color="#1E293B" mb={3}>
                  How does this work with existing faculty?
                </Heading>
                <Text color="#475569">
                  Collaboration model. Our instructors co-teach with your faculty. We upskill your team while delivering results.
                </Text>
              </Box>

              <Box p={6} bg="#F8FAFC" borderRadius="lg">
                <Heading size="sm" color="#1E293B" mb={3}>
                  Which disciplines can we start with?
                </Heading>
                <Text color="#475569">
                  Start with any of our 6 disciplines: Full-Stack & AI, Mobile Dev, Data Science, Cybersecurity, DevOps/SRE, or Digital Marketing.
                </Text>
              </Box>

              <Box p={6} bg="#F8FAFC" borderRadius="lg">
                <Heading size="sm" color="#1E293B" mb={3}>
                  What if our students don't get placed?
                </Heading>
                <Text color="#475569">
                  Rare, but if it happens, we have a support framework. Our track record shows 85%+ success, and we stand behind it.
                </Text>
              </Box>

              <Box p={6} bg="#F8FAFC" borderRadius="lg">
                <Heading size="sm" color="#1E293B" mb={3}>
                  How long is the minimum commitment?
                </Heading>
                <Text color="#475569">
                  Start with a 6-month pilot with one discipline. If it works, expand. No long-term lock-in contracts.
                </Text>
              </Box>
            </Grid>
          </VStack>
        </Container>
      </Box>

      {/* Final CTA Section */}
      <Box
        bgGradient="linear(to-br, #0F172A, #1E293B, #10B981)"
        py={{ base: 12, md: 20 }}
        color="white"
      >
        <Container maxW="container.xl">
          <VStack spacing={8} textAlign="center">
            <Heading as="h2" size="2xl" color="white">
              Transform Your College's Placement Outcomes
            </Heading>
            <Text fontSize="lg" maxW="2xl" color="#FFFFFF">
              Join 50+ colleges across India improving placements and building industry-ready graduates.
            </Text>

            <HStack spacing={4} justify="center" flexWrap="wrap">
              <CTAButton href="mailto:partnerships@ojasamirai.com" size="lg">
                Schedule Your Free Consultation
              </CTAButton>
              <CTAButton to="/" variant="outline" size="lg">
                Learn More
              </CTAButton>
            </HStack>

            <Text fontSize="sm" color="#E2E8F0" mt={4}>
              Questions? Email: partnerships@ojasamirai.com | Call: +91-9900-30-9000
            </Text>
          </VStack>
        </Container>
      </Box>

      <Footer />
    </Box>
  );
}
